import React, { useState, useEffect, useRef } from 'react';
import { mediaAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Image, Plus, Search, Trash2, Copy, ExternalLink, FolderOpen, Upload, Shield, Clock, Sparkles, RotateCw, Crop as CropIcon, Music } from 'lucide-react';
import { AudioVideoEditor } from '../../components/AudioVideoEditor';
import { toast } from 'sonner';

const LICENSE_TYPES = ['royalty_free', 'rights_managed', 'editorial', 'creative_commons', 'public_domain', 'proprietary'];

export default function AdminMedia() {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fileType, setFileType] = useState('');
  const [folder, setFolder] = useState('');

  // URL add dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ url: '', filename: '', file_type: 'image', alt_text: '', caption: '', credit: '', folder: '', tags: '' });

  // File upload
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  // Rights dialog
  const [rightsAsset, setRightsAsset] = useState(null);
  const [rightsForm, setRightsForm] = useState({ license_type: '', license_expiry: '', usage_restrictions: '', credit: '', source_url: '' });
  const [savingRights, setSavingRights] = useState(false);

  // Versions dialog
  const [versionsAsset, setVersionsAsset] = useState(null);
  const [versions, setVersions] = useState([]);
  // AI tag dialog
  const [aiTagAsset, setAiTagAsset] = useState(null);
  const [aiTagSuggestions, setAiTagSuggestions] = useState([]);
  const [aiTagSelected, setAiTagSelected] = useState([]);
  const [aiTagLoading, setAiTagLoading] = useState(false);
  // Audio/video editor
  const [audioEditAsset, setAudioEditAsset] = useState(null);
  // Canvas image editor
  const [editAsset, setEditAsset] = useState(null);
  const canvasRef = React.useRef(null);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [canvasImg, setCanvasImg] = useState(null);
  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [cropStart, setCropStart] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [search, fileType, folder]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (search) params.search = search;
      if (fileType && fileType !== 'all') params.file_type = fileType;
      if (folder && folder !== 'all') params.folder = folder;
      const res = await mediaAPI.list(params);
      setAssets(res.data.assets || []);
      setFolders(res.data.folders || []);
    } catch { toast.error('Failed to load media'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!addForm.url || !addForm.filename) { toast.error('URL and filename required'); return; }
    try {
      await mediaAPI.create({ ...addForm, tags: addForm.tags ? addForm.tags.split(',').map(s => s.trim()) : [] });
      toast.success('Asset added');
      setShowAddDialog(false);
      setAddForm({ url: '', filename: '', file_type: 'image', alt_text: '', caption: '', credit: '', folder: '', tags: '' });
      loadData();
    } catch { toast.error('Failed to add asset'); }
  };

  const handleFileUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let success = 0;
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (folder && folder !== 'all') formData.append('folder', folder);
        await mediaAPI.upload(formData);
        success++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (success) { toast.success(`${success} file(s) uploaded`); loadData(); }
  };

  const handleDelete = async (id) => {
    try { await mediaAPI.delete(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const openRights = (asset) => {
    setRightsAsset(asset);
    setRightsForm({
      license_type: asset.license_type || '',
      license_expiry: asset.license_expiry || '',
      usage_restrictions: asset.usage_restrictions || '',
      credit: asset.credit || '',
      source_url: asset.source_url || '',
    });
  };

  const saveRights = async () => {
    if (!rightsAsset) return;
    setSavingRights(true);
    try {
      await mediaAPI.updateRights(rightsAsset.id, rightsForm);
      toast.success('Rights updated');
      setRightsAsset(null);
      loadData();
    } catch { toast.error('Failed to save rights'); }
    finally { setSavingRights(false); }
  };

  const openVersions = async (asset) => {
    setVersionsAsset(asset);
    try {
      const r = await mediaAPI.getVersions(asset.id);
      setVersions(r.data.versions || []);
    } catch { setVersions([]); }
  };

  const saveVersion = async (assetId) => {
    try {
      await mediaAPI.saveVersion(assetId);
      toast.success('Version snapshot saved');
      if (versionsAsset?.id === assetId) {
        const r = await mediaAPI.getVersions(assetId);
        setVersions(r.data.versions || []);
      }
    } catch { toast.error('Failed to save version'); }
  };

  const openAiTag = async (asset) => {
    setAiTagAsset(asset);
    setAiTagSuggestions([]);
    setAiTagSelected([]);
    setAiTagLoading(true);
    try {
      const r = await mediaAPI.aiTag(asset.id);
      setAiTagSuggestions(r.data.suggestions || []);
    } catch { toast.error('AI tagging failed'); }
    finally { setAiTagLoading(false); }
  };

  const applyAiTags = async () => {
    if (!aiTagAsset || !aiTagSelected.length) return;
    try {
      await mediaAPI.applyTags(aiTagAsset.id, aiTagSelected);
      toast.success('Tags applied');
      setAiTagAsset(null);
      loadData();
    } catch { toast.error('Failed to apply tags'); }
  };

  const openImageEditor = (asset) => {
    if (asset.file_type !== 'image') { toast.info('Image editing is only available for image files'); return; }
    setEditAsset(asset);
    setRotation(0);
    setBrightness(100);
    setCanvasImg(null);
    // Load image into canvas
    setTimeout(() => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setCanvasImg(img);
        drawCanvas(img, 0, 100);
      };
      img.src = asset.url;
    }, 50);
  };

  const drawCanvas = (img, rot, bright, cont = contrast, sat = saturation, crop = null) => {
    if (!canvasRef.current || !img) return;
    const canvas = canvasRef.current;
    const isRotated = rot % 180 !== 0;
    canvas.width = isRotated ? img.naturalHeight : img.naturalWidth;
    canvas.height = isRotated ? img.naturalWidth : img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.filter = `brightness(${bright}%) contrast(${cont}%) saturate(${sat}%)`;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    if (crop && crop.w > 0 && crop.h > 0) {
      ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
    } else {
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    }
    ctx.restore();
    // Draw crop overlay
    if (cropMode && cropRect.w > 0) {
      ctx.strokeStyle = 'rgba(184,155,94,0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(cropRect.x - canvas.width / 2, cropRect.y - canvas.height / 2, cropRect.w, cropRect.h);
    }
  };

  const handleRotate = () => {
    const newRot = (rotation + 90) % 360;
    setRotation(newRot);
    drawCanvas(canvasImg, newRot, brightness);
  };

  const handleBrightness = (val) => { setBrightness(val); drawCanvas(canvasImg, rotation, val, contrast, saturation); };
  const handleContrast = (val) => { setContrast(val); drawCanvas(canvasImg, rotation, brightness, val, saturation); };
  const handleSaturation = (val) => { setSaturation(val); drawCanvas(canvasImg, rotation, brightness, contrast, val); };

  const handleCropMouseDown = (e) => {
    if (!cropMode || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    setCropStart({ x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY });
  };
  const handleCropMouseMove = (e) => {
    if (!cropMode || !cropStart || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = Math.min(cropStart.x, (e.clientX - rect.left) * scaleX);
    const y = Math.min(cropStart.y, (e.clientY - rect.top) * scaleY);
    const w = Math.abs((e.clientX - rect.left) * scaleX - cropStart.x);
    const h = Math.abs((e.clientY - rect.top) * scaleY - cropStart.y);
    setCropRect({ x, y, w, h });
    drawCanvas(canvasImg, rotation, brightness, contrast, saturation, { x, y, w, h });
  };
  const handleCropMouseUp = () => { setCropStart(null); };

  const applyCrop = () => {
    if (!cropRect.w || !canvasImg) return;
    drawCanvas(canvasImg, rotation, brightness, contrast, saturation, cropRect);
    setCropMode(false);
    toast.success('Crop applied — export to save');
  };

  const exportEditedImage = () => {
    if (!canvasRef.current || !editAsset) return;
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.9);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = editAsset.filename.replace(/\.[^.]+$/, '') + '_edited.jpg';
    a.click();
    toast.success('Edited image downloaded. Re-upload to replace the original.');
  };

  return (
    <div className="space-y-6" data-testid="admin-media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Image className="w-6 h-6 text-[#B89B5E]" /> Media Library</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage images, videos, and documents</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-file-btn"
          >
            <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading…' : 'Upload File'}
          </Button>
          <input ref={fileInputRef} type="file" multiple className="hidden" accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            onChange={e => handleFileUpload(e.target.files)} />
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-media-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">
            <Plus className="w-4 h-4 mr-2" /> Add by URL
          </Button>
        </div>
      </div>

      {/* Drag-drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${dragOver ? 'border-[#B89B5E] bg-[#B89B5E]/5' : 'border-[#E7E0D6]'}`}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-[#B89B5E] opacity-60" />
        <p className="text-sm text-[#5B616E]">Drag &amp; drop files here, or use <button className="underline text-[#121316]" onClick={() => fileInputRef.current?.click()}>browse</button></p>
        <p className="text-xs text-[#5B616E] mt-1">Images, videos, audio, and documents supported</p>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
          <Input className="pl-10" placeholder="Search media..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={fileType} onValueChange={setFileType}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
          </SelectContent>
        </Select>
        {folders.length > 0 && (
          <Select value={folder} onValueChange={setFolder}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All folders" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Folders</SelectItem>
              {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="aspect-square rounded-xl" />)}
        </div>
      ) : assets.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {assets.map(asset => (
            <Card key={asset.id} className="bg-white border-[#E7E0D6] overflow-hidden group" data-testid="media-asset-card">
              <div className="aspect-square relative bg-[#FBF7EF]">
                {asset.file_type === 'image' ? (
                  <img src={asset.url} alt={asset.alt_text || asset.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderOpen className="w-12 h-12 text-[#E7E0D6]" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-7 w-7 p-0" onClick={() => copyUrl(asset.url)} title="Copy URL"><Copy className="w-3.5 h-3.5" /></Button>
                    <a href={asset.url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-7 w-7 p-0" title="Open"><ExternalLink className="w-3.5 h-3.5" /></Button></a>
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-7 w-7 p-0" onClick={() => handleDelete(asset.id)} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-6 px-1.5 text-[10px]" onClick={() => openRights(asset)} title="Rights"><Shield className="w-3 h-3 mr-0.5" />Rights</Button>
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-6 px-1.5 text-[10px]" onClick={() => openVersions(asset)} title="Versions"><Clock className="w-3 h-3 mr-0.5" />Hist</Button>
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-6 px-1.5 text-[10px]" onClick={() => openAiTag(asset)} title="AI Tags"><Sparkles className="w-3 h-3 mr-0.5" />Tag</Button>
                    {asset.file_type === 'image' && <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-6 px-1.5 text-[10px]" onClick={() => openImageEditor(asset)} title="Edit Image"><CropIcon className="w-3 h-3 mr-0.5" />Edit</Button>}
                    {(asset.file_type === 'audio' || asset.file_type === 'video') && <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-6 px-1.5 text-[10px]" onClick={() => setAudioEditAsset(asset)} title="Edit Audio/Video"><Music className="w-3 h-3 mr-0.5" />Edit</Button>}
                  </div>
                </div>
                {asset.license_type && (
                  <div className="absolute top-1 right-1 bg-black/60 rounded px-1 py-0.5 text-[8px] text-white">{asset.license_type.replace('_', ' ')}</div>
                )}
              </div>
              <CardContent className="p-3">
                <p className="text-xs font-medium truncate">{asset.filename}</p>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{asset.file_type}</Badge>
                  {asset.folder && <Badge variant="outline" className="text-[10px]">{asset.folder}</Badge>}
                  {asset.file_size && <span className="text-[9px] text-[#5B616E]">{(asset.file_size / 1024).toFixed(1)}KB</span>}
                </div>
                {asset.credit && <p className="text-[9px] text-[#5B616E] mt-0.5 truncate">© {asset.credit}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]">
          <Image className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No media assets</p>
          <p className="text-sm mt-1">Add your first asset to get started</p>
        </div>
      )}

      {/* Add by URL */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Media Asset by URL</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">URL</label><Input value={addForm.url} onChange={e => setAddForm({ ...addForm, url: e.target.value })} placeholder="https://..." data-testid="media-url-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Filename</label><Input value={addForm.filename} onChange={e => setAddForm({ ...addForm, filename: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Type</label>
                <Select value={addForm.file_type} onValueChange={v => setAddForm({ ...addForm, file_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['image', 'video', 'document', 'audio'].map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Folder</label><Input value={addForm.folder} onChange={e => setAddForm({ ...addForm, folder: e.target.value })} placeholder="Optional" /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Alt Text</label><Input value={addForm.alt_text} onChange={e => setAddForm({ ...addForm, alt_text: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Credit / Byline</label><Input value={addForm.credit} onChange={e => setAddForm({ ...addForm, credit: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Tags (comma separated)</label><Input value={addForm.tags} onChange={e => setAddForm({ ...addForm, tags: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-media-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">Add Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rights management dialog */}
      <Dialog open={!!rightsAsset} onOpenChange={() => setRightsAsset(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-4 h-4" /> Rights &amp; Licensing</DialogTitle></DialogHeader>
          {rightsAsset && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">License type</label>
                <Select value={rightsForm.license_type || ''} onValueChange={v => setRightsForm(f => ({ ...f, license_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select license..." /></SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPES.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium mb-1 block">License expiry date</label><Input type="date" value={rightsForm.license_expiry} onChange={e => setRightsForm(f => ({ ...f, license_expiry: e.target.value }))} /></div>
              <div><label className="text-sm font-medium mb-1 block">Credit / Copyright owner</label><Input value={rightsForm.credit} onChange={e => setRightsForm(f => ({ ...f, credit: e.target.value }))} placeholder="Photographer / agency" /></div>
              <div><label className="text-sm font-medium mb-1 block">Source URL</label><Input value={rightsForm.source_url} onChange={e => setRightsForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://..." /></div>
              <div><label className="text-sm font-medium mb-1 block">Usage restrictions</label><Textarea rows={2} value={rightsForm.usage_restrictions} onChange={e => setRightsForm(f => ({ ...f, usage_restrictions: e.target.value }))} placeholder="e.g. Editorial use only, no social media" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRightsAsset(null)}>Cancel</Button>
            <Button onClick={saveRights} disabled={savingRights} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{savingRights ? 'Saving…' : 'Save Rights'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audio/Video Editor dialog */}
      {audioEditAsset && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setAudioEditAsset(null); }}>
          <div className="w-full max-w-2xl">
            <div className="flex justify-between items-center mb-2">
              <p className="text-white font-medium text-sm">{audioEditAsset.filename}</p>
              <button onClick={() => setAudioEditAsset(null)} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
            </div>
            <AudioVideoEditor
              src={audioEditAsset.url}
              mediaType={audioEditAsset.file_type}
              onExported={({ blob }) => { setAudioEditAsset(null); toast.success('Re-upload the exported file to replace the original.'); }}
            />
          </div>
        </div>
      )}

      {/* AI Tag dialog */}
      <Dialog open={!!aiTagAsset} onOpenChange={() => setAiTagAsset(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B89B5E]" /> AI Tag Suggestions</DialogTitle></DialogHeader>
          {aiTagLoading ? (
            <div className="py-6 text-center text-sm text-[#5B616E]">Analysing asset…</div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#5B616E]">Select tags to apply to <strong>{aiTagAsset?.filename}</strong>:</p>
              <div className="flex flex-wrap gap-2">
                {aiTagSuggestions.map(t => (
                  <button key={t} type="button"
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${aiTagSelected.includes(t) ? 'bg-[#121316] text-white border-[#121316]' : 'border-[#E7E0D6] text-[#5B616E] hover:border-[#B89B5E]'}`}
                    onClick={() => setAiTagSelected(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}>
                    {t}
                  </button>
                ))}
                {aiTagSuggestions.length === 0 && <p className="text-sm text-[#5B616E]">No suggestions found.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiTagAsset(null)}>Cancel</Button>
            <Button onClick={applyAiTags} disabled={!aiTagSelected.length} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Apply {aiTagSelected.length} tag{aiTagSelected.length !== 1 ? 's' : ''}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canvas image editor dialog */}
      <Dialog open={!!editAsset} onOpenChange={() => setEditAsset(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CropIcon className="w-4 h-4" /> Image Editor — {editAsset?.filename}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#121316] rounded-xl overflow-hidden max-h-72 flex items-center justify-center p-2 relative"
              style={{ cursor: cropMode ? 'crosshair' : 'default' }}>
              <canvas ref={canvasRef}
                style={{ maxWidth: '100%', maxHeight: '260px', objectFit: 'contain' }}
                className="rounded"
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
              />
              {!canvasImg && editAsset && <div className="text-[#5B616E] text-sm">Loading image…</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1">Rotation</label>
                <Button variant="outline" size="sm" onClick={handleRotate} className="w-full"><RotateCw className="w-4 h-4 mr-1" /> 90°</Button>
              </div>
              <div>
                <Button variant={cropMode ? 'default' : 'outline'} size="sm" onClick={() => { setCropMode(!cropMode); setCropRect({ x: 0, y: 0, w: 0, h: 0 }); }} className={`w-full ${cropMode ? 'bg-[#B89B5E] text-white' : ''}`}>
                  <CropIcon className="w-4 h-4 mr-1" /> {cropMode ? 'Drag to crop' : 'Crop'}
                </Button>
              </div>
              {cropMode && cropRect.w > 0 && (
                <Button size="sm" variant="outline" onClick={applyCrop} className="col-span-2 text-xs">Apply Crop Region ({Math.round(cropRect.w)}×{Math.round(cropRect.h)})</Button>
              )}
              <div className="col-span-2">
                <label className="text-xs font-medium block mb-1">Brightness ({brightness}%)</label>
                <input type="range" min={10} max={300} value={brightness} onChange={e => handleBrightness(parseInt(e.target.value, 10))} className="w-full" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Contrast ({contrast}%)</label>
                <input type="range" min={10} max={300} value={contrast} onChange={e => handleContrast(parseInt(e.target.value, 10))} className="w-full" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Saturation ({saturation}%)</label>
                <input type="range" min={0} max={300} value={saturation} onChange={e => handleSaturation(parseInt(e.target.value, 10))} className="w-full" />
              </div>
            </div>
            <p className="text-xs text-[#5B616E]">Export downloads the edited image. Re-upload it to the media library to use it on articles.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditAsset(null)}>Cancel</Button>
            <Button onClick={exportEditedImage} disabled={!canvasImg} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Download Edited Image</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsAsset} onOpenChange={() => setVersionsAsset(null)}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4" /> Asset Version History</DialogTitle></DialogHeader>
          {versionsAsset && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#5B616E]">{versionsAsset.filename}</p>
                <Button size="sm" variant="outline" onClick={() => saveVersion(versionsAsset.id)}>Snapshot current</Button>
              </div>
              {versions.length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-4">No version history yet. Click "Snapshot current" to save the current state.</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((v, i) => (
                    <div key={v.id || i} className="p-3 border border-[#E7E0D6] rounded-lg text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">v{versions.length - i}</span>
                        <span className="text-[#5B616E]">{v.version_at ? new Date(v.version_at).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-[#5B616E]">{v.filename} · {v.file_type} · {v.license_type || 'no license'}</p>
                      {v.credit && <p className="text-[#5B616E]">© {v.credit}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
